import numbro from 'numbro'
import formatDate from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'

/**
 * Parse given date string to Date object
 *
 * @param {String} value
 * @return {Date}
 */
export const parseDate = (value) => {
  const parsedISO = typeof value === 'string' ? parseISO(value) : value

  return isNaN(parsedISO) ? new Date(value) : parsedISO
}

/**
 * Format given date to date format
 *
 * @param {String} value
 * @param {String} format
 * @param {String} defaultValue
 */
export const date = (value, format = 'MMM dd, yyyy', defaultValue = null) => {
  try {
    return value ? formatDate(parseDate(value), format) : defaultValue
  } catch (error) {
    return value
  }
}

/**
 * Format given string to date time format
 *
 * @param {String} value
 * @param {String} defaultValue
 */
export const datetime = (
  value,
  defaultValue = null,
  format = 'MMM dd, yyyy hh:mma'
) => {
  return date(value, format, defaultValue)
}

/**
 * Format given number
 *
 * @param {Number} value
 * @param {Object} options
 * @return {String}
 */
export const number = (value, options = {}) => {
  if (value) {
    return numbro(value).format({
      thousandSeparated: true,
      ...options,
    })
  }

  return value
}

/**
 * Format given size bytes
 *
 * @param {Number} value
 * @param {Object} options
 * @return {String}
 */
export const bytes = (value, options = {}) => {
  if (isNaN(value)) {
    return value
  }

  return numbro(value)
    .format({
      output: 'byte',
      base: 'binary',
      spaceSeparated: true,
      mantissa: 2,
      ...options,
    })
    .replace('KiB', 'KB')
    .replace('MiB', 'MB')
    .replace('GiB', 'GB')
    .replace('TiB', 'TB')
}

/**
 * Format given number to currency
 *
 * @param {Number} value
 * @param {String} symbol
 * @return {String}
 */
export const currency = (value, symbol = 'P') => {
  // Not a number
  if (isNaN(value)) {
    return value
  }

  // Negative => P -11,00.22
  if (value < 0) {
    return `- ${symbol} ${number(Math.abs(value))}`
  }

  // Positive => P 11,00.22
  return `${symbol} ${number(Math.abs(value))}`
}

/**
 * Normalize file name
 *
 * @param {String} name
 *
 * @return {String}
 */
export const normalizeFileName = (name) => {
  try {
    return name.split('/')[name.split('/').length - 1]
  } catch (error) {
    return name
  }
}

/**
 * Date distance to now
 *
 * @param {*} date
 * @param {*} options
 * @returns
 */
export const dateDistanceToNow = (date, options = { addSuffix: true }) => {
  try {
    return formatDistanceToNow(parseDate(date), options)
  } catch (err) {
    return date
  }
}

/**
 * Parse string to number
 *
 * @param {String} numberString
 * @returns {Number}
 */
export const parseStringToInt = (numberString) => {
  if (Number.isNaN(parseInt(numberString))) {
    return null
  }

  return parseInt(numberString)
}
